import {setAppConfig} from './appConfig';
import {makeStore} from './utils/redux';

/**
 * @name initGmi
 * @desc Send in a required configuration object, an optional initial application state object, and an optional array of reducers (you can still use `utils.combineReducers` at any time).
 * @description Pass in a required application config, and get back a unified redux store with GMI state and optional initialState
 * @param {Object} config
 * @param {Object} reduxConfig {initialState, initialReducers, middleware}
 * @param {Object} pathConfig - {app, session} object containing custom paths for session and app.
 * @returns {Object} A redux [store](https://redux.js.org/api-reference/store) with complete initial app state in an [immutable](https://facebook.github.io/immutable-js/docs/) format.
 * @example
 * // Configs for GMA, Solr, and ultimately GBO endpoints, along with  necessary headers and query params to make those API calls.
 * const config = {
 *  CDA_ORIGIN_URL, // *(String)*: i.e. https://int1-webapi.natcar-np.com/cda-national/
 *  GMA_ORIGIN_URL, // *(String)*: i.e. https://int1-webapi.natcar-np.com/gma-national/
 *  GMA_HEADERS, // *(Object)*: App specific custom headers to send in GMA requests
 *  SOLR_ORIGIN_URL, // *(String)*: i.e. https://int1.location.enterprise.com/enterprise-sls/search/location/
 *  DYNAMIC_CONTENT_ORIGIN_URL, // *(String)*: i.e. https://int1-webapi.natcar-np.com/gma-national/
 *  BRAND, // *(String)*: i.e. ALAMO, NATIONAL, or ENTERPRISE
 *  CHANNEL, // *(String)*: i.e. APP or WEB
 *  COUNTRY, // *(String)*: Two letter country code; i.e. US, IE, etc.
 *  DATE_TIME_FORMAT: { // *(Object)*: See full documentation for Moment https://momentjs.com/docs/#/i18n/changing-locale/
 *    months : 'janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre'.split('_'),
 *    monthsShort : 'janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.'.split('_'),
 *    weekdays : 'dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi'.split('_'),
 *    weekdaysShort : 'dim._lun._mar._mer._jeu._ven._sam.'.split('_'),
 *    weekdaysMin : 'Di_Lu_Ma_Me_Je_Ve_Sa'.split('_'),
 *    longDateFormat : {
 *      LT : 'HH:mm',
 *      LTS : 'HH:mm:ss',
 *      L : 'DD/MM/YYYY',
 *      LL : 'D MMMM YYYY',
 *      LLL : 'D MMMM YYYY HH:mm',
 *      LLLL : 'dddd D MMMM YYYY HH:mm'
 *    },
 *    week : {
 *      dow : 1, // Monday is the first day of the week.
 *      doy : 4  // Used to determine first week of the year.
 *    }
 *  },
 *  GBO_TIME_FORMAT, // *(String)*: 'YYYY-MM-DDTHH:mm', // timestamp format expected by GBO
 *  ERROR_DEFAULT, // *(String)*: i.e. "We're sorry. Please try again later.",
 *  ERROR_STATUS, {}, // *(Object)*: Optional. i.e. {404: 'Page not found.'}
 *  STATUS_I18N_CODE, // *(Boolean)*: Optional. defaults to false. Use translated errors for GMA errors
 *  LANGUAGE, // *(String)*: Two letter language code; i.e. en, es
 *  LOCALE // *(String)*: Language and country code; i.e. en_US, es_ES
 * }
 * const reduxConfig = {
 *   initialState, // *(Object)*: i.e. {...} // will get merged into redux store via state.app
 *   initialReducers, // *(Array)*: i.e. [...] // an array of reducers
 *   middleware // *(Array|Function)*: i.e. [store => next => action => {next(action)}] // an array of middleware
 * }
 * const pathConfig = {
 *   app: ['path1', 'app'],
 *   session: ['path1', 'session']
 * }
 * const store = initGmi(config, reduxConfig, pathConfig);
 */
export function initGmi(config, reduxConfig, pathConfig = {}) {
  const {initialState = {}, initialReducers = [], middleware} = reduxConfig;
  // set brand/channel via config
  setAppConfig(config);
  return makeStore(initialState, initialReducers, middleware, pathConfig);
}
